import { useRef, useState } from "react";
import ReportMaterialTable from "../../../../../components/tables/ReportMaterialTable";
import { RectangleStackIcon } from "@heroicons/react/24/solid";
import useGetCarrierEANDOPerStateData from "./hooks/useGetCarrierEANDOPerStateData";
import { CSVLink } from "react-csv";
import DownloadButton from "../../../../../components/DownloadButton";
import { useRecoilState, useRecoilValue } from "recoil";
import { useHistory } from "react-router-dom";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import { mainSelectedTab } from "modules/CarrierAgreementMgmt/atoms";
import { CarrierAgreementMgmtTabs } from "modules/CarrierAgreementMgmt/carrierAgreementTabs";

interface StateItem {
  id?: string | number;
  states: string;
  eAndOSubmissionType: string;
  eAndOEmail: string;
  eAndOUrl: string;
}

interface DetailPanelProps {
  rowData: {
    states?: StateItem[];
  };
}

export const CarrierDetailPanel: React.FC<DetailPanelProps> = ({ rowData }) => {
  return (
    <div className="p-4 bg-gray-50">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="text-blue-600 font-semibold border-b border-gray-200">
            <th className="text-left px-3 py-2">State</th>
            <th className="text-left px-3 py-2">E&O Submission Type</th>
            <th className="text-left px-3 py-2">E&O Email Address</th>
            <th className="text-left px-3 py-2">E&O URL</th>
          </tr>
        </thead>

        <tbody>
          {rowData.states?.map((stateItem: any, index: number) => (
            <tr
              key={stateItem.id || stateItem.states}
              className="border-b border-gray-100 last:border-b-0"
            >
              <td className="px-3 py-2 text-gray-800">{stateItem.states}</td>
              <td className="px-3 py-2 text-gray-800">
                {stateItem.eAndOSubmissionType}
              </td>
              <td className="px-3 py-2 text-gray-800">
                {stateItem.eAndOEmail || "-"}
              </td>
              <td className="px-3 py-2 text-gray-800">
                {stateItem.eAndOUrl || "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const CarrierEAndOPerStateReports = () => {
  const [exportedData, setExportedData] = useState<any[]>([]);
  const { data, isLoading } = useGetCarrierEANDOPerStateData();
  const dateTimeToday = new Date(new Date().toLocaleString()).toLocaleString();
  const reportData = Array.isArray(data) ? data : [];
  const [, setSelectedIndex] = useRecoilState(mainSelectedTab);
  const history = useHistory();
  const userPermissions = useRecoilValue(permissionsSelector);
  const tableRef = useRef<any>([]);
  const [searchedData, setSearchedData] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");

  const downloadReport = (data) => {
    const searchedCarrierNames = new Set(data.map((x) => x.carrierName));

    const result = reportData.filter((x) =>
      searchedCarrierNames.has(x.carrierName)
    );
    if (!reportData) return;

    const transformed = result.map(({ tableData, ...row }) => ({
      "Carrier Name": row.carrierName,
      State: row.states,
      "E&O Submission Type": row.eAndOSubmissionType,
      "E&O Email": row.eAndOEmail,
      "E&O URL": row.eAndOUrl,
    }));

    setExportedData(transformed);
  };

  const csvReport = {
    filename: `CarrierE&O_Per_State_Report_${dateTimeToday}.csv`,
    data: exportedData || [],
  };

  const columns = [
    {
      title: "Carrier Name",
      field: "carrierName",
      render: (rowData) => (
        <div className="flex items-center justify-between w-full">
          <div
            className="cursor-pointer"
            onClick={() => {
              const url = "/CarrierAgreementMgmt/carriers";
              history.push({
                pathname: url,
                state: {
                  pathname: url,
                  carrierName: rowData?.carrierName,
                  action: "carrierE&o",
                },
              });
              window.location.reload();
              let userTabs = [
                {
                  to: "",
                },
              ];
              [...CarrierAgreementMgmtTabs].forEach((tabs) => {
                let temp = [...userPermissions].filter(
                  (user) => user === tabs.permission
                );
                if (temp.length > 0)
                  userTabs.push({
                    to: tabs.to,
                  });
              });
              userTabs.shift();
              const index = userTabs.findIndex((c) => c.to === url);
              setSelectedIndex(index);
            }}
          >
            <span className="text-quinary  font-bold underline text-sm ">
              {rowData.carrierName}
            </span>
          </div>

          <span className="ml-2 rounded-full bg-blue-800 px-2 py-1 text-[10px] font-semibold text-white">
            {rowData.states?.length || 0}{" "}
            {rowData.states?.length === 1 ? "State" : "States"}
          </span>
        </div>
      ),
    },
  ];

  const groupedByCarrier = reportData
    ? reportData.reduce((acc, item) => {
        if (!acc[item.carrierName]) acc[item.carrierName] = [];
        acc[item.carrierName].push(item);
        return acc;
      }, {})
    : {};

  const tableRows = Object.keys(groupedByCarrier).map((carrierName) => ({
    carrierName,
    states: groupedByCarrier[carrierName],
  }));

  const dataCount =
    searchText === "" ? reportData?.length ?? 0 : searchedData?.length ?? 0;

  return (
    <div className="text-xs items-center">
      <div className="mx-4 mt-4 overflow-x-auto">
        <div className="flex mb-2 gap-2 items-center">
          <RectangleStackIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
          <div className="flex flex-col leading-tight">
            <div className="text-gray-500 text-md font-medium">
              Report: Carrier Report
            </div>
            <div className="flex items-center font-bold text-lg text-primary">
              E&O Carrier Contact Per State Report
            </div>
          </div>
        </div>

        <div className="flex justify-between mb-2 mr-2">
          <div className="flex items-center gap-1">
            <span className="text-gray-500 text-sm">Total Records:</span>
            <span className="text-primary text-base font-semibold">
              {dataCount}
            </span>
          </div>
          <CSVLink {...csvReport}>
            <DownloadButton
              buttonValue="Download File"
              onClick={() => {
                downloadReport(tableRef.current.dataManager.searchedData);
              }}
              disabled={searchText !== "" && searchedData?.length === 0}
            />
          </CSVLink>
        </div>
      </div>

      <div className="box-shadow mx-4 mb-4 mt-2 grid grid-row">
        <ReportMaterialTable
          data={tableRows}
          isLoading={isLoading}
          columns={columns}
          showTitle={false}
          toolbarSearch={true}
          showToolbar={true}
          detailPanel={(rowData) => <CarrierDetailPanel rowData={rowData} />}
          tableRef={tableRef}
          onSearchChange={(text) => {
            setSearchText(text ?? "");
            const data = tableRef.current?.dataManager?.searchedData ?? [];
            setSearchedData(data);
          }}
        />
      </div>
    </div>
  );
};

export default CarrierEAndOPerStateReports;
